// Sun Valley : la carte de collection, calquée sur Absolut Cashmere (capture d'Enzo du
// 03/10/2026). Trois comportements, aucun rechargement de page.
//
//   1. Galerie. Deux flèches discrètes sur les côtés de la photo font défiler les photos
//      du produit. Elles n'apparaissent qu'au survol.
//
//   2. Bandeau au survol. En bas de la photo, toutes les pastilles de coloris sur une
//      ligne, les tailles en dessous, en texte simple. Aucun cadre, aucun bouton dessiné :
//      c'est ce qui donne l'impression de légèreté. Cliquer une taille ajoute au panier,
//      cliquer une pastille transforme la carte en ce coloris.
//
//   3. Au repos, sous la photo, une seule pastille et le nombre d'autres coloris.
//
// Les données viennent de /products/{handle}.js, gardées en mémoire : un coloris déjà
// consulté revient instantanément.
(() => {
  // Textes traduits (snippets/sv-textes-js.liquid) ; à défaut, la clé
  const T = (cle, v) => (window.svT ? window.svT(cle, v) : cle);
  // Racine de la langue affichée (« / » ou « /en/ ») : les données produit et le panier suivent la langue
  const racine = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CARTE = 'product-card, .product-card';
  const TAILLE = /^(taille|size|pointure)$/i;
  const memoire = new Map();

  const argent = (centimes, devise) => (centimes / 100).toLocaleString('fr-FR', { style: 'currency', currency: devise || 'EUR' });
  const handleDepuisLien = (href) => (href || '').split('/products/')[1]?.split('?')[0]?.split('#')[0] || '';

  async function produit(handle) {
    if (memoire.has(handle)) return memoire.get(handle);
    const p = fetch(`${racine()}products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    memoire.set(handle, p);
    return p;
  }

  // ---------------------------------------------------------------------------
  // La photo : mémoire de l'état d'origine, et affichage d'une photo précise.
  // ---------------------------------------------------------------------------
  function photo(carte) {
    const img = carte.querySelector('.product-card__image--primary');
    if (img && !img.dataset.svOrigine) {
      img.dataset.svOrigine = img.getAttribute('src') || '';
      img.dataset.svOrigineSrcset = img.getAttribute('srcset') || '';
    }
    return img;
  }

  function montrer(carte, src) {
    const img = photo(carte);
    if (!img) return;
    if (src) { img.removeAttribute('srcset'); img.src = src; }
    else {
      img.src = img.dataset.svOrigine;
      if (img.dataset.svOrigineSrcset) img.setAttribute('srcset', img.dataset.svOrigineSrcset);
    }
  }

  // ---------------------------------------------------------------------------
  // 1. Galerie à flèches
  // ---------------------------------------------------------------------------
  function galerie(carte, etat) {
    const figure = carte.querySelector('.product-card__figure');
    const media = carte.querySelector('.product-card__media');
    if (!figure || !media || figure.querySelector('.sv-fleche')) return;

    const fleche = (sens) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `sv-fleche sv-fleche--${sens}`;
      b.setAttribute('aria-label', sens === 'avant' ? T('carte.photo_precedente') : T('carte.photo_suivante'));
      b.innerHTML = sens === 'avant'
        ? '<svg viewBox="0 0 8 14" width="8" height="14" aria-hidden="true"><path d="M7 1 1 7l6 6" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>'
        : '<svg viewBox="0 0 8 14" width="8" height="14" aria-hidden="true"><path d="m1 1 6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';
      b.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const p = await produit(etat.handle);
        const photos = (p?.images || []).slice(0, 6);
        if (photos.length < 2) return;
        etat.index = (etat.index + (sens === 'avant' ? -1 : 1) + photos.length) % photos.length;
        montrer(carte, photos[etat.index]);
      });
      return b;
    };

    figure.appendChild(fleche('avant'));
    figure.appendChild(fleche('apres'));
  }

  // ---------------------------------------------------------------------------
  // 2. Le bandeau : pastilles puis tailles
  // ---------------------------------------------------------------------------
  async function ajouter(bouton, id) {
    bouton.disabled = true;
    bouton.classList.add('is-occupe');
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      if (!r.ok) throw new Error('ajout');
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      const panier = document.getElementById('cart-drawer');
      if (panier && typeof panier.show === 'function') setTimeout(() => panier.show(), 250);
      bouton.classList.add('is-ajoute');
      setTimeout(() => bouton.classList.remove('is-ajoute'), 1200);
    } catch (e) {
      bouton.classList.add('is-rate');
      setTimeout(() => bouton.classList.remove('is-rate'), 1600);
    } finally {
      bouton.disabled = false;
      bouton.classList.remove('is-occupe');
    }
  }

  function rendreTailles(ligne, p) {
    const position = (p.options || []).findIndex((o) => TAILLE.test(typeof o === 'string' ? o : o.name));
    const tailles = (p.variants || []).map((v) => ({
      id: v.id,
      nom: position >= 0 ? v.options[position] : v.title,
      dispo: v.available,
    }));
    // Un accessoire sans taille n'a pas de ligne : on n'affiche pas « Default Title ».
    if (position < 0 || tailles.length < 2) { ligne.innerHTML = ''; return; }

    ligne.innerHTML = tailles.map((t) => `<button type="button" class="sv-taille${t.dispo ? '' : ' is-epuisee'}" data-sv-ajouter="${t.id}" ${t.dispo ? '' : 'disabled'} aria-label="${t.dispo ? T('carte.ajouter_taille', { taille: t.nom }) : T('carte.taille_epuisee', { taille: t.nom })}">${t.nom}</button>`).join('');
    for (const b of ligne.querySelectorAll('[data-sv-ajouter]')) {
      b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); ajouter(b, b.dataset.svAjouter); });
    }
  }

  function remplacer(carte, etat, p, pastille) {
    const img = photo(carte);
    if (img && p.featured_image) {
      img.removeAttribute('srcset');
      img.src = p.featured_image;
      img.dataset.svOrigine = p.featured_image;
      img.dataset.svOrigineSrcset = '';
    }
    const secondaire = carte.querySelector('.product-card__image--secondary');
    if (secondaire) secondaire.remove();

    for (const lien of carte.querySelectorAll('a[href*="/products/"]')) {
      if (!lien.classList.contains('sv-pastille')) lien.setAttribute('href', p.url);
    }
    const titre = carte.querySelector('.product-title, .product-card__title, [class*="product-title"]');
    if (titre) titre.textContent = p.title;
    const prix = carte.querySelector('.price, [class*="price"]');
    if (prix && typeof p.price === 'number') prix.textContent = argent(p.price, window.Shopify?.currency?.active);

    for (const autre of carte.querySelectorAll('.sv-pastille')) {
      autre.classList.remove('is-courante');
      autre.removeAttribute('aria-current');
    }
    pastille.classList.add('is-courante');
    pastille.setAttribute('aria-current', 'true');

    // La pastille du repos prend la couleur du coloris choisi. Le nom du coloris n'est
    // écrit nulle part sur la carte, c'est la pastille qui le dit.
    const repos = carte.querySelector('[data-sv-coloris] .sv-pastille');
    if (repos) {
      repos.style.setProperty('--sv-c1', pastille.style.getPropertyValue('--sv-c1'));
      repos.style.setProperty('--sv-c2', pastille.style.getPropertyValue('--sv-c2'));
    }

    etat.handle = handleDepuisLien(p.url);
    etat.index = 0;
    rendreTailles(carte.querySelector('[data-sv-tailles]'), p);
  }

  function bandeau(carte, etat) {
    const gabarit = carte.querySelector('template[data-sv-panneau]');
    const figure = carte.querySelector('.product-card__figure');
    if (!gabarit || !figure || figure.querySelector('.sv-bandeau')) return;

    const bloc = document.createElement('div');
    bloc.className = 'sv-bandeau';

    // Un modèle d'un seul coloris a un gabarit vide : le bandeau n'affiche alors que les
    // tailles, plutôt qu'une ligne de pastilles réduite à un rond tout seul.
    const couleurs = document.createElement('div');
    couleurs.className = 'sv-bandeau__couleurs';
    couleurs.appendChild(gabarit.content.cloneNode(true));

    const tailles = document.createElement('div');
    tailles.className = 'sv-bandeau__tailles';
    tailles.setAttribute('data-sv-tailles', '');

    if (couleurs.querySelector('.sv-pastille')) bloc.append(couleurs, tailles);
    else bloc.append(tailles);
    figure.appendChild(bloc);

    for (const pastille of couleurs.querySelectorAll('.sv-pastille')) {
      const apercu = pastille.dataset.svImage;
      pastille.addEventListener('mouseenter', () => { if (apercu) montrer(carte, apercu); });
      pastille.addEventListener('mouseleave', () => montrer(carte, null));
      pastille.addEventListener('click', async (e) => {
        const handle = handleDepuisLien(pastille.getAttribute('href'));
        if (!handle) return;
        e.preventDefault();
        e.stopPropagation();
        const p = await produit(handle);
        if (!p) { window.location.href = pastille.getAttribute('href'); return; }
        remplacer(carte, etat, p, pastille);
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Montage
  // ---------------------------------------------------------------------------
  async function brancher() {
    for (const carte of document.querySelectorAll(CARTE)) {
      if (carte.dataset.svCarte) continue;
      carte.dataset.svCarte = '1';

      const etat = { handle: handleDepuisLien(carte.querySelector('a[href*="/products/"]')?.getAttribute('href')), index: 0 };
      if (!etat.handle) continue;

      photo(carte);
      galerie(carte, etat);
      bandeau(carte, etat);

      // Le thème pose un bouton « Choisir les options » pleine largeur : il ferait doublon
      // sous le bandeau. On le masque, sans le retirer, pour garder son panneau d'options.
      if (carte.querySelector('.product-card__quick-buy')) carte.classList.add('sv-carte--sobre');

      const p = await produit(etat.handle);
      const ligne = carte.querySelector('[data-sv-tailles]');
      if (p && ligne) rendreTailles(ligne, p);

      // Ni coloris ni tailles, un accessoire par exemple : le bandeau ne serait qu'une
      // bande blanche posée sur la photo. On l'enlève.
      const bloc = carte.querySelector('.sv-bandeau');
      if (bloc && !bloc.querySelector('.sv-pastille, .sv-taille')) bloc.remove();
    }
  }

  // Le thème remplace la grille au filtrage et à la pagination : il faut rebrancher les
  // cartes qui arrivent. On groupe les appels, sinon chaque bandeau que l'on insère
  // relance l'observateur, et une page de cent cartes repasse cent fois sur la grille.
  let prevu = false;
  function replanifier() {
    if (prevu) return;
    prevu = true;
    requestAnimationFrame(() => { prevu = false; brancher(); });
  }

  document.addEventListener('DOMContentLoaded', brancher);
  new MutationObserver(replanifier).observe(document.documentElement, { childList: true, subtree: true });
  brancher();
})();
